/**
 * RequireRole — TV-RV-07
 *
 * Route guard that restricts access to routes by role.
 * Redirects unauthorized users to their own home route (VIEW_CONFIG[role].home)
 * rather than a generic 403 — so a patient deep-linking to /careplan/enc-001
 * is silently sent to /waiting-room rather than shown an error.
 *
 * Usage:
 *   <RequireRole allow={['provider']}>
 *     <CarePlan />
 *   </RequireRole>
 *
 *   <RequireRole allow={['provider', 'front-desk']}>
 *     <Billing />
 *   </RequireRole>
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { getViewConfig } from './viewConfig';

/**
 * @param {object} props
 * @param {string[]}    props.allow    - roles that may access the wrapped content
 * @param {React.ReactNode} props.children
 */
export function RequireRole({ allow, children }) {
  const { user } = useAuth();
  const role = user?.role ?? 'patient';

  if (allow.includes(role)) {
    return children;
  }

  // Redirect to the user's own home — not a hard error page.
  const cfg = getViewConfig(role);
  return <Navigate to={cfg.home} replace />;
}
